import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, Button, Stepper, Step, Paper, Typography, IconButton, Grid, Divider, Chip } from "@mui/material";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import DynamicForm from "../../components/form/DynamicForm";
import { showToast } from "../../store/slices/toastSlice";
import { ROUTE_PATHS } from "../../routes/routePaths";
import { validateTenantFields } from "../../utils/validation/validation";

import {
  getBasicDetailsInputs,
  getTaxComplianceDetailsInputs,
} from "../../utils/inputs/tenant.inputs";

import {
  createNewTenant,
  fetchAllAnnualIncrementIntervalList,
  fetchAllTenantTypeList,
  getTenantDetailsById,
  updateTenantDetails,
} from "../../services/api/tenant";
import { fetchBuildingList } from "../../services/api/building";
import { getGstDetails } from "../../services/api/setting";
import { fetchAvailableRentalProperties } from "../../services/api/rental-property";
import ReviewStep from "./ReviewStep";

const STEPS = ["Tenant & Rental Details", "Tax & Compliance", "Form Preview"];

export default function CreateUpdateTenant() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const dispatch = useDispatch();

  const [activeStep, setActiveStep] = useState(0);

  const initialFormState = {
    tenant_first_name: "",
    tenant_last_name: "",
    tenant_contact: "",
    entry_date: "",
    exit_date: "",
    annual_increment_type: "",
    annual_increment_date: "",
    increment_precentage: "",
    building_id: "",
    property_id: "",
    rent_amount: "",
    apply_gst: false,
    apply_tds: false,
    tds_type: null,
    gst_number: "",
    pan_number: "",
    aadhar_number: "",
    gst_amount: "0.00",
    tds_deduction: "0.00",
    tenant_type: "",
    electricity_per_unit_charge: "",
    monthly_electricity_bill: "",
    maintenance_charges: "",
    notes: "",
    round_off: "",
    total_rent_amount: "0.00",
  };

  const [tenantDetails, setTenantDetails] = useState(initialFormState);
  const [buildingOptions, setBuildingOptions] = useState([]);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [intervalOptions, setIntervalOptions] = useState([]);
  const [tenantType, setTenantType] = useState([]);
  const [gstPercentage, setGstPercentage] = useState(0);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const buildings = await fetchBuildingList();
        setBuildingOptions(
          buildings.data.map((b) => ({
            value: b.id.toString(),
            label: b.building_name,
          }))
        );

        const properties = await fetchAvailableRentalProperties(isEditMode ? id : null);
        setPropertyOptions(properties.data || []);

        const intervals = await fetchAllAnnualIncrementIntervalList();
        setIntervalOptions(intervals.data || []);

        const tenantTypeList = await fetchAllTenantTypeList();
        setTenantType(tenantTypeList.data || []);
      } catch (error) {
        dispatch(showToast({ message: error.message, severity: "error" }));
      }

      const gstResponse = await getGstDetails();
      if (gstResponse?.data?.length > 0) {
        setGstPercentage(gstResponse.data[0].gst_percentage);
      }
    };

    const fetchTenantDetails = async () => {
      if (isEditMode) {
        try {
          const response = await getTenantDetailsById(id);
          if (response.responseCode === 200) {
            const data = response.data;
            setTenantDetails({
              ...data,
              building_id: data.building_id?.toString() || "",
              property_id: data.property_id?.toString() || "",
            });
          }
        } catch (error) {
          dispatch(showToast({ message: error.message, severity: "error" }));
        }
      }
    };

    loadOptions();
    fetchTenantDetails();
  }, [id, isEditMode, dispatch]);

  useEffect(() => {
    const { entry_date, annual_increment_type } = tenantDetails;

    if (entry_date && annual_increment_type) {
      const date = new Date(entry_date);
      date.setMonth(date.getMonth() + Number(annual_increment_type));

      setTenantDetails((prev) => ({
        ...prev,
        annual_increment_date: date.toISOString().split("T")[0],
      }));
    }
  }, [tenantDetails.entry_date, tenantDetails.annual_increment_type]);

  const calculateTotalRent = ({
    rent_amount,
    maintenance_charges,
    monthly_electricity_bill,
    apply_gst,
    apply_tds,
    tds_type,
  }) => {
    const rent = Number(rent_amount) || 0;
    const maintenance = Number(maintenance_charges) || 0;
    const electricity = Number(monthly_electricity_bill) || 0;

    let total = rent + maintenance + electricity;

    // GST
    let gst = 0;
    if (apply_gst) {
      gst = rent * (gstPercentage / 100); // or (rent + maintenance) depending on your business rule
      total += gst;
    }

    // TDS
    let tds = 0;
    if (apply_tds) {
      if (tds_type === "INDIVIDUAL") {
        tds = rent * 0.01;
      } else if (tds_type === "BUSINESS") {
        tds = rent * 0.1;
      }

      total -= tds;
    }

    const roundedTotal = Math.round(total);
    const roundOff = roundedTotal - total;

    return {
      gst_amount: gst.toFixed(2),
      tds_deduction: tds.toFixed(2),
      round_off: roundOff > 0 ? `+${roundOff.toFixed(2)}` : roundOff.toFixed(2),
      total_rent_amount: roundedTotal.toFixed(2),
    };
  };

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;

    setTenantDetails((prev) => {
      const updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "building_id") {
        updated.property_id = "";
      }

      const { gst_amount, tds_deduction, round_off, total_rent_amount } = calculateTotalRent(updated);

      updated.gst_amount = gst_amount;
      updated.tds_deduction = tds_deduction;
      updated.round_off = round_off;
      updated.total_rent_amount = total_rent_amount;

      return updated;
    });
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const validationResult = validateTenantFields(tenantDetails);
    if (!validationResult.valid) {
      dispatch(showToast({ message: validationResult.message, severity: "error" }));
      return;
    }

    const tenantPayload = {
      ...tenantDetails,
      building_id: parseInt(tenantDetails.building_id),
      property_id: parseInt(tenantDetails.property_id),
      tds_type: tenantDetails.apply_tds ? tenantDetails.tds_type : null,
    };

    try {
      let response;
      if (isEditMode) {
        response = await updateTenantDetails(id, tenantPayload);
      } else {
        response = await createNewTenant(tenantPayload);
      }

      if (response.responseCode === 200) {
        dispatch(showToast({ message: response.message, severity: "success" }));
        localStorage.setItem(isEditMode ? "editTenantSuccess" : "addNewTenantSuccess", response.message);
        navigate(ROUTE_PATHS.TENANT.LIST);
      }
    } catch (error) {
      dispatch(showToast({ message: error.message, severity: "error" }));
    }
  };

  const step1Inputs = getBasicDetailsInputs(buildingOptions, propertyOptions, tenantDetails.building_id, tenantType);
  const step2Inputs = getTaxComplianceDetailsInputs(intervalOptions);

  return (
    <Box sx={{ width: "100%", maxWidth: "lg", margin: "0 auto", mt: 2 }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          p: { xs: 3, md: 4 },
          borderRadius: "12px",
          backgroundColor: "#FFFFFF",
          boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.06)",
        }}
      >
        {/* Navigation Action Line Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <IconButton onClick={activeStep > 0 ? handleBack : () => navigate(ROUTE_PATHS.TENANT.LIST)} sx={{ p: 0, color: "#1C1E21" }}>
            <ArrowBackIosNewIcon sx={{ fontSize: "18px" }} />
          </IconButton>
          <IconButton onClick={() => navigate(ROUTE_PATHS.TENANT.LIST)} size="small" sx={{ color: "#8A9BB4" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Dynamic Titles */}
        <Typography variant="h4" sx={{ fontWeight: "700", fontSize: "28px", color: "#1C1E21", mb: 1, letterSpacing: "-0.5px" }}>
          {activeStep === 0 && "Tenant & Rental Details"}
          {activeStep === 1 && "Tax & Compliance Settings"}
          {activeStep === 2 && "Review Lease Registry Summary"}
        </Typography>

        <Typography variant="body2" sx={{ color: "#606770", fontSize: "14px", mb: 3 }}>
          {activeStep === 0 && "Provide primary identity parameters and configuration bounds for the active lease."}
          {activeStep === 1 && "Configure financial compliance mandates, statutory taxation metrics, and contracts."}
          {activeStep === 2 && "Carefully check the rent structures and parameters detailed below prior to submission."}
        </Typography>

        {/* Flat Step Line Tracker Progress */}
        <Stepper activeStep={activeStep} connector={null} sx={{ mb: 4, display: "flex", gap: 1, "& .MuiStep-root": { padding: 0, flex: 1 } }}>
          {STEPS.map((label, idx) => (
            <Step key={label}>
              <Box
                sx={{
                  height: "4px",
                  borderRadius: "2px",
                  backgroundColor: idx <= activeStep ? "#1877F2" : "#E4E6EB",
                  transition: "background-color 0.3s ease",
                }}
              />
            </Step>
          ))}
        </Stepper>

        {/* Steps Core Switch Area */}
        <Box component="form" onSubmit={(e) => e.preventDefault()}>
          {activeStep === 0 && <DynamicForm inputs={step1Inputs} values={tenantDetails} onChange={handleChange} />}
          {activeStep === 1 && <DynamicForm inputs={step2Inputs} values={tenantDetails} onChange={handleChange} />}

          {/* STEP 3: Enhanced Review Page */}
          {activeStep === 2 && (
            <ReviewStep
              tenantDetails={tenantDetails}
              buildingOptions={buildingOptions}
              propertyOptions={propertyOptions}
              gstPercentage={gstPercentage}
            />
          )}
        </Box>

        {/* Footer Navigation Buttons Row */}
        <Box sx={{ mt: 5, pt: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: "1px solid #E4EBF4" }}>
          {activeStep < STEPS.length - 1 ? (
            <Button
              variant="contained"
              fullWidth
              onClick={handleNext}
              sx={{
                backgroundColor: "#1877F2", color: "#FFFFFF", textTransform: "none", fontSize: "16px", fontWeight: "700", height: "48px", borderRadius: "8px", boxShadow: "none",
                "&.Mui-disabled": { bgcolor: "#E4E6EB" }, "&:hover": { backgroundColor: "#166FE5", boxShadow: "none" }
              }}
            >
              Continue →
            </Button>
          ) : (
            <Button
              type="button"
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              sx={{
                backgroundColor: "#42B72A", color: "#FFFFFF", textTransform: "none", fontSize: "16px", fontWeight: "700", height: "48px", borderRadius: "8px", boxShadow: "none",
                "&:hover": { backgroundColor: "#36A420", boxShadow: "none" }
              }}
            >
              {isEditMode ? "Confirm & Update Tenant" : "Commit & Register Tenant"}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

// Minimal missing component polyfill wrapper for the internal stack framework
function Stack({ children, spacing }) {
  return <Box sx={{ display: "flex", flexDirection: "column", gap: spacing }}>{children}</Box>;
}